import { useEffect, useRef } from 'react';
import { EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import Image from '@tiptap/extension-image';
import Underline from '@tiptap/extension-underline';

import { api } from '../api/http.js';
import { Icon } from './icons.jsx';

/**
 * TipTap-backed WYSIWYG editor for the broadcasts compose form.
 *
 * Output: HTML string. Push delivery strips this back to plain text
 * server-side; the email channel embeds it verbatim inside a templated
 * shell. Inline images are uploaded to R2 via the admin support-image
 * endpoint (which doubles as a generic admin image surface) and
 * inserted as <img src="…r2.dev/…">.
 *
 * Toolbar covers: bold / italic / underline / heading (H2,H3) /
 * bullet list / ordered list / link / image / undo·redo. That's the
 * intersection of "full WYSIWYG" and "things every email client
 * renders without choking" — table support, colour swatches, and
 * font sizing are deliberately out of scope here because they're
 * inconsistent across email engines and our broadcasts are short
 * announcements, not newsletters.
 *
 * The component is uncontrolled-ish: `value` seeds the initial HTML,
 * and `onChange` fires on every keystroke with the current serialised
 * HTML. Storing the doc in React state at every keystroke would force
 * full TipTap re-mounts; we set the doc once on mount and let TipTap
 * own its own state until the user resets the form.
 */
export function RichTextEditor({ value, onChange, placeholder, disabled }) {
  // Track the latest onChange so the TipTap onUpdate closure can call
  // through without forcing a re-init when the parent re-renders.
  const onChangeRef = useRef(onChange);
  useEffect(() => { onChangeRef.current = onChange; }, [onChange]);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        // Headings restricted to H2/H3 — the email shell already has
        // an H1 for the broadcast title, so admin bodies sit one level
        // below. H4-H6 would create dead UI buttons in the toolbar.
        heading: { levels: [2, 3] },
      }),
      Underline,
      Link.configure({
        openOnClick: false,             // toolbar handles the click
        autolink: true,                 // pasted bare URLs become links
        HTMLAttributes: {
          // target=_blank so the email's CTA doesn't navigate the user
          // away from the support thread or web client mid-read.
          target: '_blank',
          rel: 'noopener nofollow',
        },
      }),
      Image.configure({
        // Constrain inline images to reasonable dimensions so a 4 MB
        // phone screenshot doesn't blow up the email body. Width gets
        // an inline style so it survives the round-trip through Resend
        // → email-client renderers.
        HTMLAttributes: { style: 'max-width:100%;height:auto;border-radius:8px;' },
      }),
    ],
    content: value || '',
    editable: !disabled,
    onUpdate: ({ editor }) => {
      onChangeRef.current?.(editor.getHTML());
    },
  });

  // Push external `value` changes (e.g. form reset after send) into the
  // editor. We compare against the current doc to avoid an infinite
  // setContent/onUpdate loop.
  useEffect(() => {
    if (!editor) return;
    const current = editor.getHTML();
    if ((value || '') !== current) {
      editor.commands.setContent(value || '', false);
    }
  }, [value, editor]);

  if (!editor) return null;

  return (
    <div className={`rte ${disabled ? 'rte--disabled' : ''}`}>
      <Toolbar editor={editor} disabled={disabled} />
      <EditorContent
        editor={editor}
        className="rte-content"
        // Empty-state placeholder via CSS pseudo-element. The component's
        // global.css companion targets `.rte-content .ProseMirror p.is-editor-empty:first-child::before`.
        data-placeholder={placeholder || ''}
      />
    </div>
  );
}

// ── Toolbar ─────────────────────────────────────────────────────────

function Toolbar({ editor, disabled }) {
  const setLink = () => {
    const previous = editor.getAttributes('link').href;
    // Reuse the existing browser prompt for now; the admin app's
    // custom dialog has different ergonomics than this inline tool
    // needs. Replace with a proper popover if the broadcast UX ever
    // wants inline-link previews.
    // eslint-disable-next-line no-alert
    const url = window.prompt('Link URL', previous || 'https://');
    if (url === null) return;
    if (url === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
      return;
    }
    editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
  };

  const insertImage = async () => {
    // Use the native file picker for simplicity. The admin can pick
    // multiple at once — we upload sequentially to keep error
    // surfacing easy.
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.multiple = true;
    input.onchange = async () => {
      const files = Array.from(input.files || []);
      for (const f of files) {
        try {
          const fd = new FormData();
          fd.append('file', f);
          const res = await api('/api/admin/support/upload-image', {
            method: 'POST', body: fd, multipart: true,
          });
          if (res?.url) {
            editor.chain().focus().setImage({ src: res.url, alt: f.name }).run();
          }
        } catch (e) {
          // eslint-disable-next-line no-alert
          window.alert('Image upload failed: ' + (e?.message || e));
        }
      }
    };
    input.click();
  };

  // Tiny helper so each toolbar button stays a one-liner. `cmd` runs
  // the TipTap chain; `isActive` toggles the highlight on hover/active.
  const Btn = ({ cmd, isActive, title, children }) => (
    <button
      type="button"
      className={`rte-btn ${isActive ? 'is-active' : ''}`}
      onMouseDown={(e) => e.preventDefault()} // keep focus in editor
      onClick={cmd}
      disabled={disabled}
      title={title}
    >
      {children}
    </button>
  );

  return (
    <div className="rte-toolbar" role="toolbar" aria-label="Format">
      <Btn
        title="Bold (Ctrl+B)"
        isActive={editor.isActive('bold')}
        cmd={() => editor.chain().focus().toggleBold().run()}
      >
        <strong>B</strong>
      </Btn>
      <Btn
        title="Italic (Ctrl+I)"
        isActive={editor.isActive('italic')}
        cmd={() => editor.chain().focus().toggleItalic().run()}
      >
        <em>I</em>
      </Btn>
      <Btn
        title="Underline (Ctrl+U)"
        isActive={editor.isActive('underline')}
        cmd={() => editor.chain().focus().toggleUnderline().run()}
      >
        <u>U</u>
      </Btn>

      <span className="rte-sep" aria-hidden="true" />

      <Btn
        title="Heading 2"
        isActive={editor.isActive('heading', { level: 2 })}
        cmd={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
      >
        H2
      </Btn>
      <Btn
        title="Heading 3"
        isActive={editor.isActive('heading', { level: 3 })}
        cmd={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
      >
        H3
      </Btn>

      <span className="rte-sep" aria-hidden="true" />

      <Btn
        title="Bullet list"
        isActive={editor.isActive('bulletList')}
        cmd={() => editor.chain().focus().toggleBulletList().run()}
      >
        •&nbsp;⋯
      </Btn>
      <Btn
        title="Numbered list"
        isActive={editor.isActive('orderedList')}
        cmd={() => editor.chain().focus().toggleOrderedList().run()}
      >
        1.&nbsp;⋯
      </Btn>

      <span className="rte-sep" aria-hidden="true" />

      <Btn
        title="Link"
        isActive={editor.isActive('link')}
        cmd={setLink}
      >
        🔗
      </Btn>
      <Btn
        title="Insert image"
        cmd={insertImage}
      >
        <Icon name="eye" size={14} />
      </Btn>

      <span className="rte-spacer" />

      <Btn
        title="Undo (Ctrl+Z)"
        cmd={() => editor.chain().focus().undo().run()}
      >
        ↶
      </Btn>
      <Btn
        title="Redo (Ctrl+Shift+Z)"
        cmd={() => editor.chain().focus().redo().run()}
      >
        ↷
      </Btn>
    </div>
  );
}
